import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import { AppDatePicker } from '../../../common/AppDatePicker';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

export interface InstallmentForPay {
  id: number;
  installmentNo: number;
  dueDate: string;
  amount: number;
}

export interface LoanForPay {
  id: number;
  name: string;
  currency: string;
}

interface LoanInstallmentPayModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  loan: LoanForPay | null;
  installment: InstallmentForPay | null;
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Türk para formatı ayrıştırma: "1.000,50" → 1000.50 */
const parseAmount = (v: string): number => {
  const n = parseFloat((v || '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

/** Tutar girişini yazarken canlı biçimlendirir: "200000" → "200.000" (nokta binlik, virgül ondalık) */
const formatAmountInput = (raw: string): string => {
  let cleaned = raw.replace(/[^\d,]/g, '');
  const firstComma = cleaned.indexOf(',');
  let intPart = firstComma >= 0 ? cleaned.slice(0, firstComma) : cleaned;
  const decPart = firstComma >= 0 ? cleaned.slice(firstComma + 1).replace(/,/g, '') : null;
  intPart = intPart.replace(/^0+(?=\d)/, '');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (decPart === null) return grouped;
  return `${grouped},${decPart.slice(0, 2)}`;
};

const toFinalDate = (dateStr: string): string => {
  const selected = new Date(dateStr);
  const now = new Date();
  const isToday =
    selected.getFullYear() === now.getFullYear() &&
    selected.getMonth() === now.getMonth() &&
    selected.getDate() === now.getDate();
  const finalDate = isToday
    ? new Date()
    : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);
  return finalDate.toISOString();
};

const LoanInstallmentPayModal: React.FC<LoanInstallmentPayModalProps> = ({ open, onClose, onSuccess, loan, installment }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [source, setSource] = useState<'cashbox' | 'bank' | 'accrual'>('cashbox');
  const [cashboxes, setCashboxes] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedCashboxId, setSelectedCashboxId] = useState<string>('');
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [date, setDate] = useState(todayStr());
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gecikme faizi akışı (kullanıcı gereksinimi)
  const [addLateInterest, setAddLateInterest] = useState<'no' | 'yes'>('no');
  const [lateInterest, setLateInterest] = useState('');

  useEffect(() => {
    if (open && loan && installment) {
      setSource('cashbox');
      setSelectedCashboxId('');
      setSelectedBankId('');
      setDate(todayStr());
      setDescription('');
      setAddLateInterest('no');
      setLateInterest('');
      setError(null);
      loadSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loan, installment]);

  const loadSources = async () => {
    try {
      const [cbRes, bankRes] = await Promise.all([
        accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY),
        accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY),
      ]);
      const cbData = cbRes.data?.data || cbRes.data || [];
      const bankData = bankRes.data?.data || bankRes.data || [];
      const cur = loan?.currency || 'TRY';
      setCashboxes(cbData.filter((c: any) => (c.currency || 'TRY') === cur));
      setBankAccounts(bankData.filter((b: any) => (b.currency || 'TRY') === cur));
    } catch (err) {
      console.error('[LoanInstallmentPayModal] Kaynaklar yüklenemedi:', err);
    }
  };

  if (!loan || !installment) return null;

  const dueDate = new Date(installment.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const isOverdue = dueDay < today;
  const overdueDays = isOverdue ? Math.floor((today.getTime() - dueDay.getTime()) / 86400000) : 0;

  const lateInterestVal = addLateInterest === 'yes' ? parseAmount(lateInterest) : 0;
  const totalToPay = installment.amount + lateInterestVal;

  const fmt = (n: number) => n.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSave = async () => {
    if (addLateInterest === 'yes' && lateInterestVal <= 0) {
      setError(tt('Geçerli bir gecikme faizi tutarı giriniz.', 'Please enter a valid late interest amount.'));
      return;
    }
    if (source === 'cashbox' && !selectedCashboxId) {
      setError(tt('Lütfen bir kasa seçiniz.', 'Please select a cashbox.'));
      return;
    }
    if (source === 'bank' && !selectedBankId) {
      setError(tt('Lütfen bir banka hesabı seçiniz.', 'Please select a bank account.'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      // ⚠️ Kural: request body PascalCase
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_INSTALLMENT_PAY(installment.id), {
        PaymentSource: source,
        CashboxId: source === 'cashbox' ? parseInt(selectedCashboxId, 10) : null,
        BankAccountId: source === 'bank' ? parseInt(selectedBankId, 10) : null,
        Date: toFinalDate(date),
        LateInterestAmount: lateInterestVal > 0 ? lateInterestVal : null,
        Description: description.trim() || null,
        PaidBy: userDetails?.email || 'Sistem',
      });
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err.message || tt('Ödeme yapılamadı', 'Could not make payment'));
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{tt('Taksit Öde', 'Pay Installment')}</Typography>
          <IconButton edge="end" onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" color="textSecondary">{loan.name}</Typography>
          <Typography variant="body2">
            {tt('Taksit', 'Installment')} {installment.installmentNo} — <b>{fmt(installment.amount)} {loan.currency}</b> — {tt('Vade', 'Due')}: {dueDate.toLocaleDateString(localeCode)}
          </Typography>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Gecikme faizi sorusu — yalnızca vadesi geçmiş taksitte */}
        {isOverdue && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.main' }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              {tt(
                `Bu taksidin vadesi ${overdueDays} gün geçmiş. Banka gecikme faizi işletmiş olabilir. Gecikme faizi eklemek istiyor musunuz?`,
                `This installment is ${overdueDays} day(s) overdue. The bank may have charged late interest. Do you want to add late interest?`
              )}
            </Alert>
            <RadioGroup
              row
              value={addLateInterest}
              onChange={(e) => { setAddLateInterest(e.target.value as any); if (e.target.value === 'no') setLateInterest(''); }}
            >
              <FormControlLabel value="no" control={<Radio />} label={tt('Hayır', 'No')} disabled={loading} />
              <FormControlLabel value="yes" control={<Radio />} label={tt('Evet', 'Yes')} disabled={loading} />
            </RadioGroup>
            {addLateInterest === 'yes' && (
              <>
                <TextField
                  label={tt('Gecikme Faizi Tutarı *', 'Late Interest Amount *')}
                  value={lateInterest}
                  onChange={(e) => setLateInterest(formatAmountInput(e.target.value))}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={loading}
                  placeholder="1.500"
                />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }} color="warning.main">
                  {tt('Ödenecek Toplam', 'Total to Pay')}: {fmt(installment.amount)} + {fmt(lateInterestVal)} = {fmt(totalToPay)} {loan.currency}
                </Typography>
              </>
            )}
          </Paper>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="loan-pay-source-label">{tt('Ödeme Kaynağı *', 'Payment Source *')}</InputLabel>
          <Select
            labelId="loan-pay-source-label"
            value={source}
            label={tt('Ödeme Kaynağı *', 'Payment Source *')}
            onChange={(e) => setSource(e.target.value as any)}
            disabled={loading}
          >
            <MenuItem value="cashbox">{tt('Kasa', 'Cashbox')}</MenuItem>
            <MenuItem value="bank">{tt('Banka', 'Bank')}</MenuItem>
            <MenuItem value="accrual">{tt('Tahakkuk (Elden)', 'Accrual (By hand)')}</MenuItem>
          </Select>
        </FormControl>

        {source === 'cashbox' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="loan-pay-cashbox-label">{tt('Kasa Seçin *', 'Select Cashbox *')}</InputLabel>
            <Select
              labelId="loan-pay-cashbox-label"
              value={selectedCashboxId}
              label={tt('Kasa Seçin *', 'Select Cashbox *')}
              onChange={(e) => setSelectedCashboxId(String(e.target.value))}
              disabled={loading || cashboxes.length === 0}
            >
              {cashboxes.length === 0 ? (
                <MenuItem disabled value="">{tt('Uygun kasa bulunamadı', 'No suitable cashbox found')}</MenuItem>
              ) : cashboxes.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name} ({tt('Bakiye', 'Balance')}: {Number(c.balance || 0).toLocaleString(localeCode)} {c.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {source === 'bank' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="loan-pay-bank-label">{tt('Banka Hesabı Seçin *', 'Select Bank Account *')}</InputLabel>
            <Select
              labelId="loan-pay-bank-label"
              value={selectedBankId}
              label={tt('Banka Hesabı Seçin *', 'Select Bank Account *')}
              onChange={(e) => setSelectedBankId(String(e.target.value))}
              disabled={loading || bankAccounts.length === 0}
            >
              {bankAccounts.length === 0 ? (
                <MenuItem disabled value="">{tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}</MenuItem>
              ) : bankAccounts.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.bankName} ({tt('Bakiye', 'Balance')}: {Number(b.balance || 0).toLocaleString(localeCode)} {b.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {source === 'accrual' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt(
              'Tahakkukta kasa/banka bakiyesi değişmez, taksit ödendi olarak işaretlenir.',
              'With accrual, no cashbox/bank balance changes; the installment is only marked as paid.'
            )}
          </Alert>
        )}

        <AppDatePicker
          label={tt('İşlem Tarihi *', 'Transaction Date *')}
          value={date}
          onChange={setDate}
          fullWidth
          size="medium"
          sx={{ mb: 2 }}
          disabled={loading}
        />
        <TextField
          label={tt('Açıklama', 'Description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{tt('İptal', 'Cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {tt('Öde', 'Pay')} ({fmt(totalToPay)} {loan.currency})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanInstallmentPayModal;
